import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Instagram, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { signed } from "@/lib/share";
import paperImg from "@/themes/da/collage-paper.webp";
import playerImg from "@/themes/da/collage-player.webp";

const W = 1080;
const FORMATS = {
  post: { h: 1350, top: 110, rows: 7, rh: 74, label: "Publication 1080 × 1350" },
  story: { h: 1920, top: 260, rows: 10, rh: 82, label: "Story 1080 × 1920" },
};
const LIME = "#B7F52A";
const INK = "#0B0B0B";
const PAPER = "#EDE6D6";
const firstName = (n) => (n || "Membre").replace(/^TEST_/, "").split(/\s+/)[0].slice(0, 14);
const load = (src) => new Promise((res) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => res(null); i.src = src; });

const keyOut = (img) => {
  const c = document.createElement("canvas");
  c.width = img.width; c.height = img.height;
  const x = c.getContext("2d");
  x.drawImage(img, 0, 0);
  const d = x.getImageData(0, 0, c.width, c.height);
  const p = d.data;
  for (let i = 0; i < p.length; i += 4) {
    const [r, g, b] = [p[i], p[i + 1], p[i + 2]];
    const light = Math.min(r, g, b) > 170 && Math.max(r, g, b) - Math.min(r, g, b) < 40 && !(r > 240 && g > 240 && b > 240);
    if (light) p[i + 3] = 0;
  }
  x.putImageData(d, 0, 0);
  return c;
};

const tornRect = (ctx, x, y, w, h, fill, seed = 1) => {
  ctx.save();
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.moveTo(x, y);
  for (let i = 0; i <= 24; i += 1) ctx.lineTo(x + (w * i) / 24, y + Math.sin(i * 2.3 + seed) * 4);
  for (let i = 0; i <= 24; i += 1) ctx.lineTo(x + w - (w * i) / 24, y + h + Math.cos(i * 1.7 + seed) * 4);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};

const highlight = (ctx, x, y, w, h, color = LIME) => {
  ctx.save();
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.95;
  ctx.beginPath();
  ctx.moveTo(x, y + h * 0.35); ctx.lineTo(x + w, y); ctx.lineTo(x + w + 6, y + h * 0.6); ctx.lineTo(x + 4, y + h);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};

const slanted = (ctx, text, x, y, size, color = INK) => {
  ctx.save();
  ctx.font = `800 ${size}px "Barlow Condensed"`;
  ctx.fillStyle = color;
  ctx.transform(1, 0, -0.18, 1, y * 0.18, 0);
  ctx.fillText(text, x, y);
  ctx.restore();
};

const inkOn = (hex) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? INK : "#FFFFFF";
};

async function draw(canvas, { league, period, rows, fmt, brand }) {
  const accent = brand?.primary || LIME;
  const stroke = brand?.secondary || LIME;
  const onAccent = inkOn(accent);
  const f = { ...FORMATS[fmt] };
  const H = f.h;
  await Promise.all([document.fonts.load('800 120px "Barlow Condensed"'), document.fonts.load('700 32px "Plus Jakarta Sans"')]);
  const [paper, raw, logo] = await Promise.all([load(paperImg), load(playerImg),
    brand?.logo_url ? load(`${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`) : null]);
  const player = raw && keyOut(raw);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = PAPER; ctx.fillRect(0, 0, W, H);
  if (paper) ctx.drawImage(paper, 0, 0, W, H);
  tornRect(ctx, 50, 50, W - 100, H - 100, PAPER, 3);
  if (player) {
    const ph = fmt === "story" ? 700 : 560;
    const pw = (player.width / player.height) * ph;
    const sx = W - 60 - pw * 0.78;
    ctx.save();
    ctx.beginPath(); ctx.rect(50, 50, W - 100, H - 100); ctx.clip();
    ctx.drawImage(player, sx, f.top - 30, pw, ph);
    ctx.restore();
  }
  if (logo) {
    const s = 150, lx = W - 80 - s, ly = f.top;
    tornRect(ctx, lx - 12, ly - 12, s + 24, s + 24, "#FFFFFF", 21);
    const lf = { mode: "fill", zoom: 1, x: 50, y: 50, ...(brand.logo_fit || {}) };
    if (lf.mode === "fit") {
      const k = Math.min(s / logo.width, s / logo.height);
      ctx.drawImage(logo, lx + (s - logo.width * k) / 2, ly + (s - logo.height * k) / 2, logo.width * k, logo.height * k);
    } else {
      const k = Math.max(s / logo.width, s / logo.height);
      const vw = s / (k * lf.zoom), vh = s / (k * lf.zoom);
      ctx.drawImage(logo, (logo.width - vw) * lf.x / 100, (logo.height - vh) * lf.y / 100, vw, vh, lx, ly, s, s);
    }
  }
  const t = f.top;
  ctx.font = '700 24px "Plus Jakarta Sans"';
  const label = period.toUpperCase();
  tornRect(ctx, 80, t, ctx.measureText(label).width + 40, 52, INK, 5);
  ctx.fillStyle = PAPER;
  ctx.fillText(label, 100, t + 35);
  const leader = rows[0];
  slanted(ctx, "LE ROI DE", 80, t + 190, 140);
  slanted(ctx, "LA SEMAINE.", 80, t + 320, 140);
  highlight(ctx, 90, t + 335, 520, 20, stroke);
  const tag = firstName(leader.display_name).toUpperCase();
  ctx.font = '800 64px "Barlow Condensed"';
  const tw = ctx.measureText(tag).width;
  tornRect(ctx, 80, t + 385, tw + 60, 82, accent, 7);
  slanted(ctx, tag, 108, t + 449, 64, onAccent);
  tornRect(ctx, 100 + tw + 50, t + 405, 190, 46, INK, 8);
  ctx.fillStyle = PAPER; ctx.font = '800 32px "Barlow Condensed"';
  ctx.fillText(`${signed(leader.points || 0)} PTS`, 100 + tw + 68, t + 440);
  const list = rows.slice(0, f.rows);
  const lastRank = rows[rows.length - 1].rank;
  const room = H - 200 - (t + 564);
  const rh = Math.min(fmt === "story" ? 120 : 96, Math.max(f.rh, Math.floor(room / Math.max(list.length, 1))));
  const y0 = t + 510 + Math.max(0, Math.floor((room - rh * list.length) / 2));
  f.rh = rh;
  tornRect(ctx, 70, y0, W - 140, 46, INK, 9);
  ctx.fillStyle = PAPER; ctx.font = '800 26px "Barlow Condensed"';
  ctx.fillText("RANG", 100, y0 + 32); ctx.fillText("JOUEUR", 210, y0 + 32);
  ctx.textAlign = "right"; ctx.fillText("POINTS", W - 100, y0 + 32); ctx.textAlign = "left";
  list.forEach((r, i) => {
    const y = y0 + 54 + i * f.rh;
    tornRect(ctx, 70, y, W - 140, f.rh - 10, i === 0 ? accent : i % 2 ? "#E3DAC6" : "#F4EEE1", i + 11);
    const c = i === 0 ? onAccent : INK;
    slanted(ctx, String(r.rank ?? "–"), 104, y + f.rh / 2 + 12, 48, c);
    const name = firstName(r.display_name).toUpperCase();
    slanted(ctx, name, 210, y + f.rh / 2 + 12, 46, c);
    if (rows.length > 1 && r.rank === lastRank && lastRank !== rows[0].rank) {
      ctx.font = '800 46px "Barlow Condensed"';
      const nw = ctx.measureText(name).width;
      tornRect(ctx, 230 + nw, y + f.rh / 2 - 20, 170, 34, "#D7261E", i + 3);
      ctx.fillStyle = "#FFF"; ctx.font = '800 24px "Barlow Condensed"';
      ctx.fillText("LANTERNE ROUGE", 240 + nw, y + f.rh / 2 + 5);
    }
    ctx.textAlign = "right";
    slanted(ctx, signed(r.points || 0), W - 100, y + f.rh / 2 + 14, 52, c);
    ctx.textAlign = "left";
  });
  const fy = H - 120;
  ctx.textAlign = "center";
  const title = (brand ? league.toUpperCase() : "MA PETITE LIGUE").slice(0, 30);
  slanted(ctx, title, W / 2, fy, 52);
  ctx.font = '800 52px "Barlow Condensed"';
  const hw = Math.min(ctx.measureText(title).width, W - 200);
  highlight(ctx, W / 2 - hw / 2, fy + 10, hw, 12, stroke);
  ctx.fillStyle = INK; ctx.font = '700 22px "Plus Jakarta Sans"';
  ctx.fillText(brand ? "Propulsé par Ma Petite Ligue · mapetiteligue.com · @mapetiteligue" : `${league.toUpperCase().slice(0, 32)} · mapetiteligue.com · @mapetiteligue`, W / 2, fy + 52);
  ctx.textAlign = "left";
}

export default function InstagramCard({ league, open, onOpenChange }) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [drawing, setDrawing] = useState(false);
  const [fmt, setFmt] = useState("post");
  useEffect(() => {
    if (!open) { setOffset(0); return; }
    setData(null); setError("");
    api.get(`/leagues/${league.id}/standings`, { params: { period: "week", offset } }).then(({ data: d }) => setData(d)).catch((e) => setError(errMsg(e)));
  }, [open, offset, league.id]);
  const rows = data && data.rows.some((r) => r.resolved > 0) ? data.rows.filter((r) => r.rank) : [];
  useEffect(() => {
    if (!data || !rows.length || !ref.current) return;
    let live = true;
    setDrawing(true);
    draw(ref.current, { league: league.name, period: data.period.label, rows, fmt, brand: league.brand }).finally(() => { if (live) setDrawing(false); });
    return () => { live = false; };
  }, [data, fmt]); // eslint-disable-line react-hooks/exhaustive-deps
  const exportCard = () => ref.current.toBlob(async (blob) => {
    const file = new File([blob], `classement-${fmt}-${league.name.replace(/\W+/g, "-").toLowerCase()}.png`, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], title: "Classement de la semaine" }); return; } catch (e) { if (e.name === "AbortError") return; }
    }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = file.name; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    toast.success("Image téléchargée");
  }, "image/png");
  const pill = (on) => `rounded-full border px-3 py-1.5 text-xs font-semibold ${on ? "border-[#B7F52A] bg-[#B7F52A] text-[#05070D]" : "border-white/10 text-slate-300"}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-white/10 bg-[#131B30] sm:max-w-lg" data-testid="instagram-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl font-extrabold uppercase"><Instagram size={20} className="text-[#B7F52A]" /> Carte Instagram</DialogTitle>
          <DialogDescription className="text-xs text-slate-400">Style collage, prénoms des joueurs. Demande l'accord des membres avant de publier. Illustration générée, aucun vrai joueur.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2" data-testid="instagram-formats">
          {Object.entries(FORMATS).map(([k, f]) => (
            <button key={k} onClick={() => setFmt(k)} aria-pressed={fmt === k} data-testid={`instagram-format-${k}`} className={pill(fmt === k)}>{f.label}</button>))}
        </div>
        <div className="flex flex-wrap gap-2">
          {[[0, "Cette semaine"], [-1, "Semaine dernière"]].map(([o, l]) => (
            <button key={o} onClick={() => setOffset(o)} aria-pressed={offset === o} data-testid={`instagram-week-${o === 0 ? "current" : "previous"}`} className={pill(offset === o)}>{l}</button>))}
        </div>
        {error && <p className="text-sm text-red-300">{error}</p>}
        {data && !rows.length && <p className="text-sm text-slate-300" data-testid="instagram-empty">Aucun point sur cette semaine pour l'instant : la carte sera disponible après les premiers résultats officiels.</p>}
        {data && rows.length > 0 && (<>
          <canvas ref={ref} width={W} height={FORMATS[fmt].h} className={`mx-auto rounded-xl border border-white/10 ${fmt === "story" ? "w-[min(100%,300px)]" : "w-full"}`} aria-label="Aperçu de la carte du classement" data-testid="instagram-canvas" />
          <button onClick={exportCard} disabled={drawing} className="mpl-btn-primary" data-testid="instagram-export"><Download size={15} /> Partager ou télécharger</button>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
